import { useCommand } from '@cqrs/vue-composables-cqrs'
import * as c from '@DataSharing/Shares/commands-core'
import { isRef, ref, Ref } from 'vue'

export function useDeleteShareCommandV1(
	id?: string | Ref<string> | (() => string), 
	deletedBy?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _deletedBy = isRef<string>(deletedBy) ? deletedBy : ref<string>((typeof deletedBy === 'function') ? deletedBy() : deletedBy)

	const result = {
		id: _id,
		deletedBy: _deletedBy,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof deletedBy === 'function') { _deletedBy.value = deletedBy() }

			return new c.DeleteShareCommandV1(_id.value, _deletedBy.value)
		})
	}

	return {
		...result,
		authorizedDeleteShareCommandV1: result.authorized,
		deleteShareCommandV1: result.command,
		completedDeleteShareCommandV1: result.completed,
		errorDeleteShareCommandV1: result.error,
		invokeDeleteShareCommandV1: result.invoke,
		invokingDeleteShareCommandV1: result.invoking,
		succeedDeleteShareCommandV1: result.succeed,
		validationErrorsDeleteShareCommandV1: result.validationErrors,
		onSucceedDeleteShareCommandV1: result.onSucceed,
	}
}

export function usePauseSharingCommandV1(
	id?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)

	const result = {
		id: _id,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }

			return new c.PauseSharingCommandV1(_id.value)
		})
	}

	return {
		...result,
		authorizedPauseSharingCommandV1: result.authorized,
		pauseSharingCommandV1: result.command,
		completedPauseSharingCommandV1: result.completed,
		errorPauseSharingCommandV1: result.error,
		invokePauseSharingCommandV1: result.invoke,
		invokingPauseSharingCommandV1: result.invoking,
		succeedPauseSharingCommandV1: result.succeed,
		validationErrorsPauseSharingCommandV1: result.validationErrors,
		onSucceedPauseSharingCommandV1: result.onSucceed,
	}
}

export function useResumeSharingCommandV1(
	id?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)

	const result = {
		id: _id,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }

			return new c.ResumeSharingCommandV1(_id.value)
		})
	}

	return {
		...result,
		authorizedResumeSharingCommandV1: result.authorized,
		resumeSharingCommandV1: result.command,
		completedResumeSharingCommandV1: result.completed,
		errorResumeSharingCommandV1: result.error,
		invokeResumeSharingCommandV1: result.invoke,
		invokingResumeSharingCommandV1: result.invoking,
		succeedResumeSharingCommandV1: result.succeed,
		validationErrorsResumeSharingCommandV1: result.validationErrors,
		onSucceedResumeSharingCommandV1: result.onSucceed,
	}
}

export function useSendEmailConfirmationCommandV1(
	id?: string | Ref<string> | (() => string), 
	email?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _email = isRef<string>(email) ? email : ref<string>((typeof email === 'function') ? email() : email)

	const result = {
		id: _id,
		email: _email,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof email === 'function') { _email.value = email() }

			return new c.SendEmailConfirmationCommandV1(_id.value, _email.value)
		})
	}

	return {
		...result,
		authorizedSendEmailConfirmationCommandV1: result.authorized,
		sendEmailConfirmationCommandV1: result.command,
		completedSendEmailConfirmationCommandV1: result.completed,
		errorSendEmailConfirmationCommandV1: result.error,
		invokeSendEmailConfirmationCommandV1: result.invoke,
		invokingSendEmailConfirmationCommandV1: result.invoking,
		succeedSendEmailConfirmationCommandV1: result.succeed,
		validationErrorsSendEmailConfirmationCommandV1: result.validationErrors,
		onSucceedSendEmailConfirmationCommandV1: result.onSucceed,
	}
}

export function useSendNotificationCommandV1(
	id?: string | Ref<string> | (() => string), 
	message?: string | Ref<string> | (() => string), 
	emails?: any | Ref<any> | (() => any), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _message = isRef<string>(message) ? message : ref<string>((typeof message === 'function') ? message() : message)
	const _emails = isRef<any>(emails) ? emails : ref<any>((typeof emails === 'function') ? emails() : emails)

	const result = {
		id: _id,
		message: _message,
		emails: _emails,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof message === 'function') { _message.value = message() }
			if (typeof emails === 'function') { _emails.value = emails() }

			return new c.SendNotificationCommandV1(_id.value, _message.value, _emails.value)
		})
	}

	return {
		...result,
		authorizedSendNotificationCommandV1: result.authorized,
		sendNotificationCommandV1: result.command,
		completedSendNotificationCommandV1: result.completed,
		errorSendNotificationCommandV1: result.error,
		invokeSendNotificationCommandV1: result.invoke,
		invokingSendNotificationCommandV1: result.invoking,
		succeedSendNotificationCommandV1: result.succeed,
		validationErrorsSendNotificationCommandV1: result.validationErrors,
		onSucceedSendNotificationCommandV1: result.onSucceed,
	}
}

export function useShareDataCommandV1(
	id?: string | Ref<string> | (() => string), 
	organizationUnitId?: string | Ref<string> | (() => string), 
	workspaceId?: string | Ref<string> | (() => string), 
	sharedBy?: string | Ref<string> | (() => string), 
	sharedAt?: any | null | Ref<any | null> | (() => any | null), 
	sharedFor?: any | null | Ref<any | null> | (() => any | null), 
	sharedWith?: any | Ref<any> | (() => any), 
	resourceProviderName?: string | Ref<string> | (() => string), 
	resourceId?: string | Ref<string> | (() => string), 
	emailsProviderName?: string | Ref<string> | (() => string), 
	emailsProviderConfig?: string | Ref<string> | (() => string), 
	emailsProviderGroups?: any | Ref<any> | (() => any), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _organizationUnitId = isRef<string>(organizationUnitId) ? organizationUnitId : ref<string>((typeof organizationUnitId === 'function') ? organizationUnitId() : organizationUnitId)
	const _workspaceId = isRef<string>(workspaceId) ? workspaceId : ref<string>((typeof workspaceId === 'function') ? workspaceId() : workspaceId)
	const _sharedBy = isRef<string>(sharedBy) ? sharedBy : ref<string>((typeof sharedBy === 'function') ? sharedBy() : sharedBy)
	const _sharedAt = isRef<any | null>(sharedAt) ? sharedAt : ref<any | null>((typeof sharedAt === 'function') ? sharedAt() : sharedAt)
	const _sharedFor = isRef<any | null>(sharedFor) ? sharedFor : ref<any | null>((typeof sharedFor === 'function') ? sharedFor() : sharedFor)
	const _sharedWith = isRef<any>(sharedWith) ? sharedWith : ref<any>((typeof sharedWith === 'function') ? sharedWith() : sharedWith)
	const _resourceProviderName = isRef<string>(resourceProviderName) ? resourceProviderName : ref<string>((typeof resourceProviderName === 'function') ? resourceProviderName() : resourceProviderName)
	const _resourceId = isRef<string>(resourceId) ? resourceId : ref<string>((typeof resourceId === 'function') ? resourceId() : resourceId)
	const _emailsProviderName = isRef<string>(emailsProviderName) ? emailsProviderName : ref<string>((typeof emailsProviderName === 'function') ? emailsProviderName() : emailsProviderName)
	const _emailsProviderConfig = isRef<string>(emailsProviderConfig) ? emailsProviderConfig : ref<string>((typeof emailsProviderConfig === 'function') ? emailsProviderConfig() : emailsProviderConfig)
	const _emailsProviderGroups = isRef<any>(emailsProviderGroups) ? emailsProviderGroups : ref<any>((typeof emailsProviderGroups === 'function') ? emailsProviderGroups() : emailsProviderGroups)

	const result = {
		id: _id,
		organizationUnitId: _organizationUnitId,
		workspaceId: _workspaceId,
		sharedBy: _sharedBy,
		sharedAt: _sharedAt,
		sharedFor: _sharedFor,
		sharedWith: _sharedWith,
		resourceProviderName: _resourceProviderName,
		resourceId: _resourceId,
		emailsProviderName: _emailsProviderName,
		emailsProviderConfig: _emailsProviderConfig,
		emailsProviderGroups: _emailsProviderGroups,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof organizationUnitId === 'function') { _organizationUnitId.value = organizationUnitId() }
			if (typeof workspaceId === 'function') { _workspaceId.value = workspaceId() }
			if (typeof sharedBy === 'function') { _sharedBy.value = sharedBy() }
			if (typeof sharedAt === 'function') { _sharedAt.value = sharedAt() }
			if (typeof sharedFor === 'function') { _sharedFor.value = sharedFor() }
			if (typeof sharedWith === 'function') { _sharedWith.value = sharedWith() }
			if (typeof resourceProviderName === 'function') { _resourceProviderName.value = resourceProviderName() }
			if (typeof resourceId === 'function') { _resourceId.value = resourceId() }
			if (typeof emailsProviderName === 'function') { _emailsProviderName.value = emailsProviderName() }
			if (typeof emailsProviderConfig === 'function') { _emailsProviderConfig.value = emailsProviderConfig() }
			if (typeof emailsProviderGroups === 'function') { _emailsProviderGroups.value = emailsProviderGroups() }

			return new c.ShareDataCommandV1(_id.value, _organizationUnitId.value, _workspaceId.value, _sharedBy.value, _sharedAt.value, _sharedFor.value, _sharedWith.value, _resourceProviderName.value, _resourceId.value, _emailsProviderName.value, _emailsProviderConfig.value, _emailsProviderGroups.value)
		})
	}

	return {
		...result,
		authorizedShareDataCommandV1: result.authorized,
		shareDataCommandV1: result.command,
		completedShareDataCommandV1: result.completed,
		errorShareDataCommandV1: result.error,
		invokeShareDataCommandV1: result.invoke,
		invokingShareDataCommandV1: result.invoking,
		succeedShareDataCommandV1: result.succeed,
		validationErrorsShareDataCommandV1: result.validationErrors,
		onSucceedShareDataCommandV1: result.onSucceed,
	}
}

export function useUpdateSharedWithCommandV1(
	id?: string | Ref<string> | (() => string), 
	sharedWith?: any | Ref<any> | (() => any), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _sharedWith = isRef<any>(sharedWith) ? sharedWith : ref<any>((typeof sharedWith === 'function') ? sharedWith() : sharedWith)

	const result = {
		id: _id,
		sharedWith: _sharedWith,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof sharedWith === 'function') { _sharedWith.value = sharedWith() }

			return new c.UpdateSharedWithCommandV1(_id.value, _sharedWith.value)
		})
	}

	return {
		...result,
		authorizedUpdateSharedWithCommandV1: result.authorized,
		updateSharedWithCommandV1: result.command,
		completedUpdateSharedWithCommandV1: result.completed,
		errorUpdateSharedWithCommandV1: result.error,
		invokeUpdateSharedWithCommandV1: result.invoke,
		invokingUpdateSharedWithCommandV1: result.invoking,
		succeedUpdateSharedWithCommandV1: result.succeed,
		validationErrorsUpdateSharedWithCommandV1: result.validationErrors,
		onSucceedUpdateSharedWithCommandV1: result.onSucceed,
	}
}

